import AES from 'crypto-js/aes'
import ENC from 'crypto-js/enc-utf8'
import Decimal from 'decimal.js';
import { Bank, CashInAndOutMethod, MerchantSection, ProfessionalAccountType, SectionStatus } from 'types';
import { v4 as uuidv4 } from 'uuid';

const encryption = {
    key: process.env.REACT_APP_STORAGE_ENCRYPTION_KEY as string,
    module: AES,
    enc: ENC
}

export const securelyCacheData = (key: string, value: any): void => {
    localStorage.setItem(key, (encryption.module.encrypt(JSON.stringify(value), encryption.key).toString()))
};

export const securelyRetrieveData = (key: string): any => {
    let gotItem = localStorage.getItem(key)
    if (gotItem !== null)
        return JSON.parse(encryption.module.decrypt(gotItem, encryption.key).toString(encryption.enc));
    return undefined;
}

export const securelyDeleteData = (key: string): void => localStorage.removeItem(key);

export const customClearTimeout = (timeout: NodeJS.Timeout | undefined) => {
    if (timeout) clearTimeout(timeout)
};

export const generateSecureRandomValue = (): string => uuidv4().toString();

export const getCustomDeviceId = (): string => {
    let deviceId = securelyRetrieveData("deviceId");
    if (deviceId === undefined) {
        deviceId = generateSecureRandomValue();
        securelyCacheData("deviceId", deviceId);
    }
    return deviceId;
}

export const separateStringBySpaces = (input: string): string => {
    const length = input.length;
    const groups: string[] = [];

    let i = 0;

    if (length % 2 === 0) {
        while (i < length) {
            groups.push(input.slice(i, i + 2));
            i += 2;
        }
    } else {
        while (i < length) {
            groups.push(input.slice(i, i + 3));
            i += 3;
        }
    }
    return `\u202A${groups.join(' ')}\u202C`;
}

export const getMethodName = (method: CashInAndOutMethod) => {
    switch (method) {
        case CashInAndOutMethod.ChaabiCash: return "Chaabi Cash";
        case CashInAndOutMethod.Tasshilat: return "Tasshilat";
    }
}

export const getMethodLogo = (method: CashInAndOutMethod) => {
    switch (method) {
        case CashInAndOutMethod.ChaabiCash: return require("assets/images/chaabi_cash_logo.png");
        case CashInAndOutMethod.Tasshilat: return require("assets/images/tasshilat_rectangular_logo.png");
    }
}

export const addSpacingToRIB = (str: string) => {
    if (str.length === 24)
        return str.slice(0, 3) + ' ' + str.slice(3, -2) + " " + str.slice(-2,);
    return str;
};

export const banks: { [key: string]: Bank | undefined } = require('assets/banks/RIB.json');

export const findBankByCode = (code: string): Bank | undefined => {
    if (code.length > 5) {
        const bankInfo = banks[code.slice(0, 6)];
        if (bankInfo) return bankInfo
    }
    if (code.length > 2) {
        const bankInfo = banks[code.slice(0, 3)];
        if (bankInfo) return bankInfo;
    }
    return undefined;
};

export const formatAmountString = (amountStr: string) => {
    // Split integer and decimal parts
    let [integerPart, decimalPart] = amountStr.split(".");
    if (!decimalPart) {
        // No decimal part, add .00
        decimalPart = "00";
    } else if (decimalPart.length === 1) {
        // Only 1 decimal digit, add one more 0
        decimalPart += "0";
    }
    // Add spaces every 3 digits in the integer part
    const formattedInteger = parseInt(integerPart, 10)
        .toLocaleString("fr-FR") // French locale uses spaces

    // Recombine with decimal if present
    return `${formattedInteger},${decimalPart}`;
};

export const convertFileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(',')[1];
            resolve(base64Data);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

export const centsToDirhamsString = (cents: number): string => {
    if (!Number.isInteger(cents))
        return cents.toString();

    return new Decimal(cents)
        .div(100)
        .toFixed(2)
        .replace('.', ',');
};

export const isMerchantSectionActive = (
  merchant: ProfessionalAccountType,
  section: MerchantSection
): boolean => {
  switch (section) {
    case MerchantSection.DELIVERY:
      return merchant.deliverySectionStatus === SectionStatus.ACTIVE;

    case MerchantSection.ANALYTICS:
      return merchant.analyticsSectionStatus === SectionStatus.ACTIVE;

    default:
      return false;
  }
};